import axios from 'axios'
import { crmAuthHttp } from '@/api/crm-auth'
import { normalizeSlotConfig } from '@/utils/slotInteraction'

export const SLOT_V2_API_URL = process.env.VUE_APP_SLOT_V2_API_URL || 'https://mgrebedgdxhppnfojipb.supabase.co/functions/v1/slot'
export const SLOT_MOCK_ENABLED = process.env.NODE_ENV === 'development' && process.env.VUE_APP_SLOT_MODE === 'mock'
export const SLOT_LIVE_TEST_ENABLED = process.env.NODE_ENV === 'development' && process.env.VUE_APP_SLOT_MODE === 'live-test'
export const SLOT_V2_ENABLED = process.env.VUE_APP_SLOT_MODE === 'v2' || SLOT_MOCK_ENABLED || SLOT_LIVE_TEST_ENABLED

// 沿用 CRM client 的登入 Bearer；身分與期限由後端決定。
export function createContextTokenProvider({ path = process.env.VUE_APP_SLOT_CONTEXT_PATH || '/api/external-auth/ads-context', http = crmAuthHttp } = {}) {
  return async scope => {
    if (!/^\/(?!\/)/.test(path) || path.includes('\\')) throw new Error('廣告授權接口必須使用 CRM 相對路徑')
    const body = { slot: scope.slot }
    if (scope.creative_id) body.creative_id = scope.creative_id
    if (scope.response_id) body.response_id = scope.response_id
    const { data } = await http.post(path, body, { timeout: 10000 })
    const token = data && data.context_token
    const expiresAt = data && data.expires_at
    const context = data && data.context
    if (typeof token !== 'string' || !token.trim() || /\s/.test(token) ||
        !Number.isInteger(expiresAt) || expiresAt <= Date.now() / 1000 || expiresAt > Date.now() / 1000 + 901 ||
        !context || context.channel !== 'crm' || context.slot !== scope.slot ||
        typeof context.clinic_id !== 'string' || !context.clinic_id || !['clinic', 'doctor'].includes(context.id_type) ||
        !(context.viewer === null || (typeof context.viewer === 'string' && context.viewer.length > 0)) ||
        ['creative_id', 'response_id'].some(key => context[key] !== body[key])) {
      throw new Error('CRM 廣告授權回應格式不符或已過期')
    }
    return { token, context }
  }
}

function contextMatches(context, identity) {
  return ['clinic_id', 'id_type', 'viewer'].every(key => context[key] === identity[key])
}

// 每次取得皆重新請求；到期更新／401 重試沿用此接口，不保留跨帳號 token 快取。
export const getSlotContextToken = createContextTokenProvider()

export function createSlotClient({ getToken = getSlotContextToken, http, wait = ms => new Promise(resolve => setTimeout(resolve, ms)) } = {}) {
  const client = http || axios.create({ baseURL: SLOT_V2_API_URL, withCredentials: false, timeout: 10000 })
  delete client.defaults.params
  delete client.defaults.headers.common.Authorization
  delete client.defaults.headers.Authorization

  async function request(scope, send, isCurrent = () => true) {
    let refresh = false
    for (let attempt = 0; attempt < 3; attempt++) {
      if (!isCurrent()) throw new Error('廣告上下文已切換')
      const auth = await getToken(scope, { refresh })
      const token = typeof auth === 'string' ? auth : auth.token
      const context = typeof auth === 'string' ? null : auth.context
      if (scope.identity && context && !contextMatches(context, scope.identity)) throw new Error('廣告身分已變更，請重新載入')
      if (!isCurrent()) throw new Error('廣告上下文已切換')
      if (typeof token !== 'string' || !token || /[\r\n]/.test(token)) throw new Error('廣告授權格式不符')
      try {
        const response = await send({ Authorization: `Bearer ${token}` })
        if (!isCurrent()) throw new Error('廣告上下文已切換')
        return { data: response.data, context }
      } catch (error) {
        if (!isCurrent()) throw error
        const status = error.response && error.response.status
        if (attempt === 2 || (status && status !== 401 && status !== 429 && status < 500)) throw error
        if (status === 401) {
          if (refresh) throw error
          refresh = true
        } else await wait(250 * (attempt + 1))
      }
    }
  }
  return {
    async getConfig(slot, isCurrent) {
      const { data, context } = await request({ channel: 'crm', slot }, headers => client.get('', {
        params: { action: 'config', schema_version: 2, channel: 'crm', slot }, headers,
      }), isCurrent)
      const config = normalizeSlotConfig(data, slot)
      return config && context ? { ...config, context } : config
    },
    async recordEvent(event, isCurrent) {
      // 固定整筆內容，重送不產生新 ID，也不帶入切換後身分。
      const body = JSON.stringify(event)
      const { data: result } = await request({ channel: 'crm', slot: event.slot, identity: event }, headers => client.post('', body, {
        headers: { ...headers, 'Content-Type': 'application/json' },
      }), isCurrent)
      if (!result || result.ok !== true || result.event_id !== event.event_id) throw new Error('事件未確認保存')
      return result
    },
    async getFrameToken(scope, options = { refresh: false }) {
      const auth = await getToken(scope, options)
      if (auth.context && scope.identity && !contextMatches(auth.context, scope.identity)) throw new Error('廣告身分已變更，請重新載入')
      return typeof auth === 'string' ? auth : auth.token
    },
  }
}

export function createInteractiveSlotClient() {
  // 直接使用編譯期條件，讓 production 不打包 mock 程式與假資料。
  if (process.env.NODE_ENV === 'development' && process.env.VUE_APP_SLOT_MODE === 'mock') {
    return require('@/dev/slotMock').createSlotMockClient()
  }
  if (process.env.NODE_ENV === 'development' && process.env.VUE_APP_SLOT_MODE === 'live-test') {
    return require('@/dev/slotLiveTest').createLiveTestClient(createSlotClient, createContextTokenProvider)
  }
  return createSlotClient()
}
